import { Box, Chip, MenuItem, Select, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CheckCircle, Cancel, PowerSettingsNew } from "@mui/icons-material";
import Table from "../../components/Table";
import ConfirmDialog from "../../components/ConfirmDialog";
import theme from "../../theme/theme";
import { fetchUsers, updateUserStatus } from "../../api/UserService";

const formatDesignation = (d) => {
  if (!d) return "";
  return d
    .split("_")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
};

// Combined User Type filter — values either map to a designation or to user_type=customer
const USER_TYPE_OPTIONS = [
  { value: "", label: "All Users" },
  { value: "sales", label: "Sales", kind: "designation" },
  { value: "service", label: "Service", kind: "designation" },
  { value: "engineer", label: "Engineer", kind: "designation" },
  { value: "sub_admin", label: "Sub Admin", kind: "designation" },
  { value: "others", label: "Others", kind: "designation" },
  { value: "customer", label: "Customer", kind: "user_type" },
];

const UsersManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [links, setLinks] = useState([]);
  const [meta, setMeta] = useState({ from: 0, to: 0, total: 0 });

  const [search, setSearch] = useState("");
  const [userTypeFilter, setUserTypeFilter] = useState("");
  const [status, setStatus] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [updating, setUpdating] = useState(false);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = { per_page: perPage };
      if (search) params.search = search;
      if (userTypeFilter) {
        const opt = USER_TYPE_OPTIONS.find((o) => o.value === userTypeFilter);
        if (opt?.kind === "designation") {
          params.designation = userTypeFilter;
        } else if (opt?.kind === "user_type") {
          params.user_type = userTypeFilter;
        }
      }
      if (status !== "") params.is_active = status;
      const response = await fetchUsers(currentPage, params);
      setUsers(response.data.data);
      setLinks(response.data.links);
      setMeta({ from: response.data.from, to: response.data.to, total: response.data.total });
    } catch (error) {
      toast.error(error?.message || "Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [perPage]);

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, userTypeFilter, status, perPage]);

  const openStatusConfirm = (user) => {
    setTarget(user);
    setConfirmOpen(true);
  };

  const handleConfirmStatusChange = async () => {
    if (!target) return;
    setUpdating(true);
    try {
      await updateUserStatus(target.id, !target.is_active);
      toast.success(
        target.is_active
          ? "User deactivated and signed out from all devices"
          : "User re-activated",
      );
      setConfirmOpen(false);
      setTarget(null);
      loadUsers();
    } catch (error) {
      toast.error(error?.message || "Failed to update user status");
    } finally {
      setUpdating(false);
    }
  };

  const columns = [
    { field: "name", headerName: "Name" },
    { field: "email", headerName: "Email" },
    { field: "phone", headerName: "Phone" },
    {
      field: "user_type",
      headerName: "Type",
      render: (row) => {
        if (row.user_type === "employee") {
          const label = formatDesignation(row.designation) || "Employee";
          return (
            <Chip
              label={label}
              size="small"
              color="primary"
              variant="outlined"
            />
          );
        }
        if (row.user_type === "customer") {
          return (
            <Chip
              label="Customer"
              size="small"
              color="secondary"
              variant="outlined"
            />
          );
        }
        return <Chip label="—" size="small" variant="outlined" />;
      },
    },
    {
      field: "is_active",
      headerName: "Status",
      render: (row) =>
        row.is_active ? (
          <Chip
            icon={<CheckCircle />}
            label="Active"
            color="success"
            size="small"
          />
        ) : (
          <Chip
            icon={<Cancel />}
            label="Inactive"
            color="error"
            size="small"
          />
        ),
    },
  ];

  const actions = [
    {
      icon: <PowerSettingsNew />,
      label: "Toggle Status",
      onClick: (row) => openStatusConfirm(row),
      backgroundColor: theme.button.editBackgroundColor,
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box component="h1" sx={{ m: 0 }}>
          All Users
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Search name, email, phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setCurrentPage(1);
                loadUsers();
              }
            }}
          />
          <Select
            size="small"
            value={userTypeFilter}
            onChange={(e) => {
              setUserTypeFilter(e.target.value);
              setCurrentPage(1);
            }}
            displayEmpty
            sx={{ minWidth: 160 }}
          >
            {USER_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setCurrentPage(1);
            }}
            displayEmpty
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All statuses</MenuItem>
            <MenuItem value={1}>Active</MenuItem>
            <MenuItem value={0}>Inactive</MenuItem>
          </Select>
        </Box>
      </Box>

      <Table
        columns={columns}
        data={users}
        loading={loading}
        links={links}
        setCurrentPage={setCurrentPage}
        actions={actions}
        meta={meta}
        perPage={perPage}
        setPerPage={setPerPage}
      />

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setTarget(null);
        }}
        onConfirm={handleConfirmStatusChange}
        title={target?.is_active ? "Deactivate user" : "Re-activate user"}
        message={
          target?.is_active
            ? `Deactivate ${target?.name}? They will be signed out from all devices and blocked from logging in until re-activated.`
            : `Re-activate ${target?.name}? They will be able to log in again.`
        }
        confirmText={target?.is_active ? "Deactivate" : "Re-activate"}
        loading={updating}
      />
    </Box>
  );
};

export default UsersManagementPage;
